import { ref, computed } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import dayjs from 'dayjs'
import { useDate } from '@/composables/useDate'
import { useUserStore } from '@/stores/user.stores'
import { useSystemVersionStore } from '@/stores/systemVersion.stores'
import { SETTLEMENT_TIMEZONES, type SettlementTimezone } from '@/constants/settlement-timezones'

export const useDataSettingStore = defineStore('dataSetting', () => {
  const { today } = useDate()
  const userStore = useUserStore()
  const { isNewSystem, isNewApi } = storeToRefs(useSystemVersionStore())

  const startDate = ref<string>(today())
  const endDate = ref<string>(today())
  // 不從 storesSettlementTimezone 快取取初始值：那是上一家店留下來的值，各頁會在自己的
  // GetStoreTimeZone 回來後覆蓋成這家店真正的結算時間
  const startTime = ref<string>('00:00')
  const endTime = ref<string>('00:00')
  const selectedTimeZoneId = ref<SettlementTimezone | undefined>(undefined)

  // OP#2459：兩個來源都沒有值時 default 給 UTC，避免送出空字串查詢參數
  const currentTimeZoneId = computed(
    () => selectedTimeZoneId.value || userStore.userInfo.timeZoneId || 'UTC'
  )

  const currentTimezoneLabel = computed(
    () =>
      SETTLEMENT_TIMEZONES.find((tz) => tz.value === currentTimeZoneId.value)?.label ??
      currentTimeZoneId.value
  )

  // isNewSystem 只決定小時篩選 UI 有沒有顯示，真正決定送出格式的是 isNewApi——isNewApi=false
  // 時打的是只吃純日期的 V1，isNewSystem/isNewApi 在 Sidebar 可獨立切換，isNewSystem=true 但
  // isNewApi=false 時若還併入小時，會把 datetime 誤送進 V1
  const combineDateTime = (date: string, time: string) =>
    isNewSystem.value && isNewApi.value
      ? dayjs(`${date} ${time}`).format('YYYY-MM-DDTHH:mm:ss')
      : dayjs(date).format('YYYY-MM-DD')

  const setTimeRange = (payload: { start?: string; end?: string }) => {
    if (payload.start !== undefined) {
      startTime.value = payload.start
    }

    if (payload.end !== undefined) {
      endTime.value = payload.end
    }
  }

  /**
   * 設定日期範圍
   * 驗證日期格式是否為 YYYY-MM-DD
   * 允許只傳入起始日期或結束日期其中之一
   */
  const setDateRange = (payload: { start?: string | null; end?: string | null }) => {
    const dateFormatRegex = /^\d{4}-\d{2}-\d{2}$/

    if (
      payload.start !== undefined &&
      payload.start !== null &&
      !dateFormatRegex.test(payload.start)
    ) {
      console.warn('起始日期格式必須為 YYYY-MM-DD')
      return
    }

    if (payload.end !== undefined && payload.end !== null && !dateFormatRegex.test(payload.end)) {
      console.warn('結束日期格式必須為 YYYY-MM-DD')
      return
    }

    if (payload.start !== undefined && payload.start !== null) {
      startDate.value = payload.start
    }

    if (payload.end !== undefined && payload.end !== null) {
      endDate.value = payload.end
    }
  }

  // 搜尋條件不做快取：Pinia store 活在整個 app 生命週期，各頁進頁時重置回預設，避免上一頁
  // （或上一家店）的日期/時間殘留成下一頁的真實查詢條件。時間退回 00:00，各頁自己會用
  // GetStoreTimeZone 換算出的結算時間覆蓋
  const reset = () => {
    startDate.value = today()
    endDate.value = today()
    startTime.value = '00:00'
    endTime.value = '00:00'
  }

  return {
    startDate,
    endDate,
    startTime,
    endTime,
    selectedTimeZoneId,
    currentTimeZoneId,
    currentTimezoneLabel,
    combineDateTime,
    setDateRange,
    setTimeRange,
    reset
  }
})
